import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { SkeletonLine } from "@ui-components/Skeleton/GenericSkeleton";
import ExtenderPopLinkBandwidthChart from "./ExtenderPopLinkBandwidthChart";
import ExtenderPopPorts from "./ExtenderPopPorts";
import ErrorFetchingItems from "@shared/components/ErrorFetching";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { EditableTagList } from "../Shared/NameTagEdit/EditableTagList";
import { fetchExtenderPop } from "./requests/request";
import { extenderPopStatusPresentation } from "./ExtenderPopData";

/** Customer-facing carrier leg as served by the detail endpoint (CTX-359). */
interface CarrierLeg {
  id: number;
  carrierCode: string;
  name: string;
  source: "lm" | "carr";
  capacity: string | null;
  circuitId: string | null;
  serviceRequestId: string | null;
}

/** Customer-facing link of the router: backbone uplink or HA interconnect. */
interface RouterLink {
  kind: "backbone-uplink" | "ha-interconnect";
  capacity: string | null;
  destination: string | null;
  carrierCode: string | null;
  carrierName: string | null;
  portAllocationId: number;
}

interface ExtenderPopDetail {
  id: number;
  serviceId: string;
  name: string | null;
  serviceSubtype: string;
  status: string;
  tenantId: number;
  site: {
    datacenterId: string;
    name: string | null;
    displayName: string | null;
    city: string | null;
  } | null;
  haPeer: { serviceId: string; name: string | null } | null;
  links: RouterLink[];
  carrierAccess: CarrierLeg[];
  subscription: {
    term: number | null;
    term_unit: string | null;
    renewDate: string;
    autoRenew: string;
    status: string | null;
  } | null;
}

/** Same row markup PortDetailsCard uses: label left, value right, border-b. */
const DetailRow: React.FC<{
  label: string;
  value?: React.ReactNode;
  isLast?: boolean;
}> = ({ label, value, isLast = false }) => (
  <div
    className={`flex justify-between w-full py-1 ${isLast ? "" : "border-b"}`}
  >
    <h3 className="text-sm text-semantic-text-primary">{label}</h3>
    <div className="flex items-center gap-2 min-w-0">
      {value != null && value !== "" ? (
        typeof value === "string" || typeof value === "number" ? (
          <p className="text-sm text-semantic-text-primary">{value}</p>
        ) : (
          value
        )
      ) : (
        <p className="text-sm text-gray-500">-</p>
      )}
    </div>
  </div>
);

const LoadingCard: React.FC = () => (
  <GenericCard
    isCollapsible={false}
    bodyContent={
      <div className="flex flex-col gap-3 py-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonLine key={i} className="h-4 w-full" />
        ))}
      </div>
    }
  />
);

// The picker path of EditableTagList never reaches the legacy handlers; they
// exist only to satisfy the combined prop contract.
const legacyTagsNoop = async () => undefined;

/**
 * Location like the Ports POP cell. The inventory displayName already
 * carries the human location ("Televisa SNA (San Angel, MX)"), so nothing
 * is appended to it.
 */
const formatLocation = (site: ExtenderPopDetail["site"]): string | null =>
  site?.displayName || site?.name || null;

/**
 * Detail view for one Extender PoP, homologated with Port Details: the same
 * two-column grid of cards (details left, Status right) plus the service's
 * links. Customer-facing on purpose — no internal hostnames or hardware
 * identity; what shows is the service, its carrier accesses with speed, and
 * the backbone uplinks.
 */
const ExtenderPopDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const extenderPopId = Number(searchParams.get("extenderPopId"));
  const { tenantId } = useAppSelector((state) => state.selection);
  const api = useAxiosAuth0();

  const [detail, setDetail] = useState<ExtenderPopDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * One bandwidth chart per link (uplinks first, HA interconnect last — the
   * order the backend serves). Labels carry destination/carrier/speed.
   */
  const linkCharts = useMemo(
    () =>
      (detail?.links ?? []).map((link) => ({
        id: link.portAllocationId,
        label:
          link.kind === "ha-interconnect"
            ? `HA interconnect${link.capacity ? ` (${link.capacity})` : ""}`
            : `Uplink${link.destination ? ` — ${link.destination}` : ""}${
                link.carrierName || link.capacity
                  ? ` (${[link.carrierName, link.capacity].filter(Boolean).join(", ")})`
                  : ""
              }`,
      })),
    [detail?.links]
  );

  useEffect(() => {
    if (!Number.isInteger(extenderPopId) || extenderPopId <= 0) {
      setError("Missing Extended PoP id");
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetchExtenderPop(api, extenderPopId, tenantId);
        if (cancelled) return;
        if (response.data?.success && response.data.data) {
          setDetail(response.data.data as ExtenderPopDetail);
        } else {
          setError(response.data?.message || "Could not load the details");
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Network error");
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [api, extenderPopId, tenantId]);

  if (error) {
    return <ErrorFetchingItems text={error} />;
  }

  if (!detail) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 px-4">
        <LoadingCard />
        <LoadingCard />
      </div>
    );
  }

  const status = extenderPopStatusPresentation(detail.status);
  const uplinks = detail.links.filter((l) => l.kind === "backbone-uplink");
  const interconnect = detail.links.find(
    (l) => l.kind === "ha-interconnect"
  );

  return (
    <div className="px-4 overflow-y-auto h-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {/* Card 1 — homolog of "Port Details" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Extended PoP Details"
            bodyContent={
              <div className="space-y-2">
                <DetailRow label="ID" value={detail.serviceId} />
                <DetailRow
                  label="Name"
                  value={
                    <GenericTag
                      label={detail.name || "—"}
                      variant="neutral"
                      size="small"
                      minWidth="100px"
                      className="text-center"
                    />
                  }
                />
                <div className="flex justify-between w-full py-1 border-b">
                  <h3 className="text-sm text-semantic-text-primary">Tag</h3>
                  <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
                    <EditableTagList
                      tags={[]}
                      onUpdate={legacyTagsNoop}
                      resourceType="ke-extender-pop"
                      resourceId={detail.id}
                      tenantId={detail.tenantId}
                    />
                  </div>
                </div>
                <DetailRow
                  label="Location"
                  value={formatLocation(detail.site)}
                />
                <DetailRow
                  label="Carrier Access"
                  value={
                    detail.carrierAccess.length > 0
                      ? detail.carrierAccess
                          .map((leg) => leg.name)
                          .join(", ")
                      : null
                  }
                />
                <DetailRow
                  label="HA Peer"
                  value={
                    detail.haPeer
                      ? `${detail.haPeer.serviceId}${detail.haPeer.name ? ` — ${detail.haPeer.name}` : ""}`
                      : null
                  }
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 2 — homolog of "Status" */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible={false}
            headerContent="Status"
            bodyContent={
              <div className="space-y-2">
                <DetailRow
                  label="Service Status"
                  value={
                    <GenericTag
                      variant={status.variant}
                      label={status.label}
                      size="small"
                    />
                  }
                />
                <DetailRow
                  label="Term"
                  value={
                    detail.subscription?.term
                      ? `${detail.subscription.term} ${detail.subscription.term_unit ?? ""}`
                      : null
                  }
                />
                <DetailRow
                  label="Auto renew"
                  value={detail.subscription?.autoRenew ?? null}
                />
                <DetailRow
                  label="Renew Date"
                  value={
                    detail.subscription?.renewDate &&
                    detail.subscription.renewDate !== "N/A"
                      ? new Date(
                          detail.subscription.renewDate
                        ).toLocaleDateString()
                      : null
                  }
                />
                <DetailRow
                  label="Subscription Status"
                  value={detail.subscription?.status ?? null}
                  isLast
                />
              </div>
            }
          />
        </div>

        {/* Card 3 — backbone uplinks + HA interconnect */}
        <div className="flex w-full self-start">
          <GenericCard
            isCollapsible
            initialExpanded
            headerContent="Backbone Interconnection"
            bodyContent={
              detail.links.length === 0 ? (
                <p className="py-1.5 text-sm text-semantic-text-secondary">
                  No backbone links detected for this Extended PoP.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-semantic-text-secondary">
                        <th className="py-1.5 pr-4 font-normal">Link</th>
                        <th className="py-1.5 pr-4 font-normal">Speed</th>
                        <th className="py-1.5 font-normal">Carrier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uplinks.map((link) => (
                        <tr
                          key={link.portAllocationId}
                          className="border-t border-semantic-border-secondary text-semantic-text-primary"
                        >
                          <td className="py-1.5 pr-4">
                            {`Uplink${link.destination ? ` — ${link.destination}` : ""}`}
                          </td>
                          <td className="py-1.5 pr-4">
                            {link.capacity ?? "-"}
                          </td>
                          <td className="py-1.5">{link.carrierName ?? "-"}</td>
                        </tr>
                      ))}
                      {interconnect && (
                        <tr className="border-t border-semantic-border-secondary text-semantic-text-primary">
                          <td className="py-1.5 pr-4">HA interconnect</td>
                          <td className="py-1.5 pr-4">
                            {interconnect.capacity ?? "-"}
                          </td>
                          <td className="py-1.5">-</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )
            }
          />
        </div>
      </div>

      {/* One Bandwidth Utilization per link — the uplinks and the HA
          interconnect, each with its own time controls. */}
      {linkCharts.length > 0 && (
        <div className="flex flex-col gap-4 pb-4">
          {linkCharts.map((link) => (
            <ExtenderPopLinkBandwidthChart
              key={link.id}
              extenderPopId={detail.id}
              linkId={link.id}
              title={link.label}
              tenantId={tenantId}
            />
          ))}
        </div>
      )}

      {/* The customer ports delivered through this Extended PoP; their
          services live on each port's own detail. */}
      <div className="pb-4">
        <ExtenderPopPorts extenderPopId={detail.id} tenantId={tenantId} />
      </div>
    </div>
  );
};

export default ExtenderPopDetails;
